import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import DictationInput from '../../components/DictationInput';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  Home, User, Phone, Mail, Calendar, Heart, Shield, Briefcase,
  CheckCircle, ChevronRight, ChevronLeft, Send, AlertTriangle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { key: 'personal', label: 'Personal Info', icon: User },
  { key: 'situation', label: 'Current Situation', icon: Home },
  { key: 'recovery', label: 'Recovery', icon: Heart },
  { key: 'justice', label: 'Justice Involvement', icon: Shield },
  { key: 'additional', label: 'Additional', icon: Send },
];

export default function OurSquareApplyPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    full_name: '', preferred_name: '', date_of_birth: '', phone: '', email: '',
    gender: '', race_ethnicity: '', primary_language: 'English',
    emergency_contact_name: '', emergency_contact_phone: '', emergency_contact_relationship: '',
    current_living_situation: '', has_children: false, number_of_children: 0, children_ages: '',
    employment_status: '', income_source: '',
    sobriety_date: '', drug_of_choice: '', currently_in_treatment: false,
    treatment_program_name: '', recovery_support_involvement: '',
    justice_involved: '', supervision_type: '', officer_name: '', officer_phone: '', court_jurisdiction: '',
    reason_for_applying: '', how_heard_about_us: '', additional_info: '',
  });

  const update = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const canProceed = () => {
    if (step === 0) return form.full_name.trim() && (form.phone.trim() || form.email.trim());
    return true;
  };

  const handleSubmit = async () => {
    if (!form.full_name.trim()) return toast.error('Full name is required');
    if (!form.phone.trim() && !form.email.trim()) return toast.error('Phone or email is required');
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/our-square/public-apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Submission failed');
      }
      setSubmitted(true);
      toast.success('Application submitted successfully!');
    } catch (e) {
      toast.error(e.message || 'Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-white to-teal-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-0 shadow-xl rounded-3xl" data-testid="apply-success">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>Application Received!</h2>
            <p className="text-sm text-muted-foreground mt-2">
              Thank you for applying to Our Square Transitional Housing. Our team will review your application and contact you within 3-5 business days.
            </p>
            <Button className="mt-6 bg-cyan-600 hover:bg-cyan-700 text-white rounded-full" onClick={() => navigate('/login')} data-testid="apply-success-login">
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-white to-teal-50">
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-3">
            <img src="/images/our-square-logo.png" alt="Our Square" className="h-20 w-auto" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
            Our Square Housing Application
          </h1>
          <p className="text-sm text-muted-foreground mt-2">Transitional Living & Sober Living for Women in Recovery</p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-1 mb-6" data-testid="apply-steps">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const isActive = i === step;
            const isDone = i < step;
            return (
              <div key={s.key} className="flex items-center">
                <button
                  onClick={() => i <= step && setStep(i)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                    isActive ? 'bg-cyan-600 text-white shadow-md' :
                    isDone ? 'bg-cyan-100 text-cyan-700' :
                    'bg-slate-100 text-slate-400'
                  }`}
                  disabled={i > step}
                >
                  {isDone ? <CheckCircle size={12} /> : <Icon size={12} />}
                  <span className="hidden sm:inline">{s.label}</span>
                </button>
                {i < STEPS.length - 1 && <ChevronRight size={14} className="text-slate-300 mx-0.5" />}
              </div>
            );
          })}
        </div>

        <Card className="border-0 shadow-lg rounded-2xl" data-testid="apply-form">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              {(() => { const Icon = STEPS[step].icon; return <Icon size={16} className="text-cyan-600" />; })()}
              {STEPS[step].label}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">

            {/* STEP 0: Personal Info */}
            {step === 0 && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5 col-span-2 sm:col-span-1">
                    <Label>Full Legal Name *</Label>
                    <Input value={form.full_name} onChange={e => update('full_name', e.target.value)} placeholder="Full legal name" data-testid="apply-full-name" />
                  </div>
                  <div className="space-y-1.5 col-span-2 sm:col-span-1">
                    <Label>Preferred Name</Label>
                    <Input value={form.preferred_name} onChange={e => update('preferred_name', e.target.value)} placeholder="Name you go by" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Date of Birth</Label>
                    <Input type="date" value={form.date_of_birth} onChange={e => update('date_of_birth', e.target.value)} data-testid="apply-dob" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Gender</Label>
                    <Select value={form.gender} onValueChange={v => update('gender', v)}>
                      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="female">Female</SelectItem>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="non_binary">Non-Binary</SelectItem>
                        <SelectItem value="prefer_not">Prefer Not to Say</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Phone *</Label>
                    <Input value={form.phone} onChange={e => update('phone', e.target.value)} placeholder="(555) 555-5555" data-testid="apply-phone" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Email *</Label>
                    <Input type="email" value={form.email} onChange={e => update('email', e.target.value)} placeholder="email@example.com" data-testid="apply-email" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Race/Ethnicity</Label>
                    <Input value={form.race_ethnicity} onChange={e => update('race_ethnicity', e.target.value)} placeholder="Race/Ethnicity" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Primary Language</Label>
                    <Input value={form.primary_language} onChange={e => update('primary_language', e.target.value)} placeholder="English" />
                  </div>
                </div>
                <div className="border-t pt-3">
                  <p className="text-xs font-semibold text-muted-foreground mb-2">Emergency Contact</p>
                  <div className="grid grid-cols-3 gap-3">
                    <Input value={form.emergency_contact_name} onChange={e => update('emergency_contact_name', e.target.value)} placeholder="Contact Name" />
                    <Input value={form.emergency_contact_phone} onChange={e => update('emergency_contact_phone', e.target.value)} placeholder="Contact Phone" />
                    <Input value={form.emergency_contact_relationship} onChange={e => update('emergency_contact_relationship', e.target.value)} placeholder="Relationship" />
                  </div>
                </div>
              </>
            )}

            {/* STEP 1: Current Situation */}
            {step === 1 && (
              <>
                <div className="space-y-1.5">
                  <Label>Current Living Situation</Label>
                  <Select value={form.current_living_situation} onValueChange={v => update('current_living_situation', v)}>
                    <SelectTrigger data-testid="apply-living-situation"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="homeless">Homeless / Unsheltered</SelectItem>
                      <SelectItem value="shelter">Emergency Shelter</SelectItem>
                      <SelectItem value="transitional">Transitional Housing</SelectItem>
                      <SelectItem value="with_family">Staying with Family/Friends</SelectItem>
                      <SelectItem value="incarcerated">Currently Incarcerated</SelectItem>
                      <SelectItem value="treatment_facility">Treatment Facility</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-3">
                  <Checkbox checked={form.has_children} onCheckedChange={v => update('has_children', v)} id="has-children" />
                  <Label htmlFor="has-children">I have children</Label>
                </div>
                {form.has_children && (
                  <div className="grid grid-cols-2 gap-3 pl-6">
                    <div className="space-y-1.5">
                      <Label>Number of Children</Label>
                      <Input type="number" min="0" value={form.number_of_children} onChange={e => update('number_of_children', parseInt(e.target.value) || 0)} />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Children's Ages</Label>
                      <Input value={form.children_ages} onChange={e => update('children_ages', e.target.value)} placeholder="e.g. 3, 7, 12" />
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Employment Status</Label>
                    <Select value={form.employment_status} onValueChange={v => update('employment_status', v)}>
                      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="employed_full">Employed Full-Time</SelectItem>
                        <SelectItem value="employed_part">Employed Part-Time</SelectItem>
                        <SelectItem value="unemployed_seeking">Unemployed - Seeking</SelectItem>
                        <SelectItem value="unemployed_not_seeking">Unemployed - Not Seeking</SelectItem>
                        <SelectItem value="disabled">Disabled / Unable to Work</SelectItem>
                        <SelectItem value="student">Student</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Income Source</Label>
                    <Input value={form.income_source} onChange={e => update('income_source', e.target.value)} placeholder="e.g. Employment, SSI, TANF" />
                  </div>
                </div>
              </>
            )}

            {/* STEP 2: Recovery */}
            {step === 2 && (
              <>
                <div className="bg-cyan-50 border border-cyan-200 rounded-xl p-3 text-sm text-cyan-800 flex items-start gap-2">
                  <Heart size={16} className="mt-0.5 shrink-0" />
                  <span>This information helps us match you with the right support. Everything shared is confidential.</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Sobriety Date</Label>
                    <Input type="date" value={form.sobriety_date} onChange={e => update('sobriety_date', e.target.value)} data-testid="apply-sobriety-date" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Drug of Choice</Label>
                    <Select value={form.drug_of_choice} onValueChange={v => update('drug_of_choice', v)}>
                      <SelectTrigger data-testid="apply-doc"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="alcohol">Alcohol</SelectItem>
                        <SelectItem value="opioids">Opioids / Heroin</SelectItem>
                        <SelectItem value="methamphetamine">Methamphetamine</SelectItem>
                        <SelectItem value="cocaine">Cocaine / Crack</SelectItem>
                        <SelectItem value="marijuana">Marijuana</SelectItem>
                        <SelectItem value="prescription">Prescription Drugs</SelectItem>
                        <SelectItem value="multiple">Multiple Substances</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                        <SelectItem value="none">None / N/A</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Checkbox checked={form.currently_in_treatment} onCheckedChange={v => update('currently_in_treatment', v)} id="in-treatment" />
                  <Label htmlFor="in-treatment">Currently in a treatment program</Label>
                </div>
                {form.currently_in_treatment && (
                  <div className="space-y-1.5 pl-6">
                    <Label>Treatment Program Name</Label>
                    <Input value={form.treatment_program_name} onChange={e => update('treatment_program_name', e.target.value)} placeholder="Program name" />
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label>Recovery Support Involvement</Label>
                  <DictationInput value={form.recovery_support_involvement} onChange={v => update('recovery_support_involvement', v)} placeholder="AA/NA meetings, sponsor, peer support, church, etc." rows={2} showCleanup={false} data-testid="apply-recovery-support" />
                </div>
              </>
            )}

            {/* STEP 3: Justice Involvement */}
            {step === 3 && (
              <>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800 flex items-start gap-2">
                  <Shield size={16} className="mt-0.5 shrink-0" />
                  <span>Are you currently on probation, post-release supervision, parole, or any other form of justice-involved supervision?</span>
                </div>
                <div className="space-y-1.5">
                  <Label>Justice Involvement Status *</Label>
                  <Select value={form.justice_involved} onValueChange={v => update('justice_involved', v)}>
                    <SelectTrigger data-testid="apply-justice"><SelectValue placeholder="Select status" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="probation">On Probation</SelectItem>
                      <SelectItem value="post_release">Post-Release Supervision</SelectItem>
                      <SelectItem value="parole">On Parole</SelectItem>
                      <SelectItem value="community_control">Community Control</SelectItem>
                      <SelectItem value="drug_court">Drug Court</SelectItem>
                      <SelectItem value="other_supervision">Other Supervision</SelectItem>
                      <SelectItem value="none">No Current Supervision</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {form.justice_involved && form.justice_involved !== 'none' && (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>Supervision Type</Label>
                      <Input value={form.supervision_type} onChange={e => update('supervision_type', e.target.value)} placeholder="e.g. Standard Probation" />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Officer Name</Label>
                      <Input value={form.officer_name} onChange={e => update('officer_name', e.target.value)} placeholder="Probation/Parole Officer" />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Officer Phone</Label>
                      <Input value={form.officer_phone} onChange={e => update('officer_phone', e.target.value)} placeholder="(555) 555-5555" />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Court Jurisdiction</Label>
                      <Input value={form.court_jurisdiction} onChange={e => update('court_jurisdiction', e.target.value)} placeholder="e.g. Maricopa County" />
                    </div>
                  </div>
                )}
              </>
            )}

            {/* STEP 4: Additional */}
            {step === 4 && (
              <>
                <div className="space-y-1.5">
                  <Label>Why are you applying to Our Square? *</Label>
                  <DictationInput value={form.reason_for_applying} onChange={v => update('reason_for_applying', v)} placeholder="Tell us why you're seeking transitional housing and what your goals are (speak or type)" rows={4} data-testid="apply-reason" />
                </div>
                <div className="space-y-1.5">
                  <Label>How did you hear about us?</Label>
                  <Select value={form.how_heard_about_us} onValueChange={v => update('how_heard_about_us', v)}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="referral_agency">Referral Agency / Case Manager</SelectItem>
                      <SelectItem value="court">Court / Probation Officer</SelectItem>
                      <SelectItem value="treatment">Treatment Facility</SelectItem>
                      <SelectItem value="friend_family">Friend or Family</SelectItem>
                      <SelectItem value="social_media">Social Media</SelectItem>
                      <SelectItem value="website">Website</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Anything else you'd like us to know?</Label>
                  <DictationInput value={form.additional_info} onChange={v => update('additional_info', v)} placeholder="Additional information, special needs, or questions (speak or type)" rows={3} showCleanup={false} />
                </div>
              </>
            )}

            {/* Navigation */}
            <div className="flex items-center justify-between pt-4 border-t">
              {step > 0 ? (
                <Button variant="outline" className="rounded-full gap-1" onClick={() => setStep(step - 1)} data-testid="apply-prev">
                  <ChevronLeft size={14} /> Previous
                </Button>
              ) : (
                <Button variant="ghost" className="rounded-full" onClick={() => navigate('/login')} data-testid="apply-back-login">Back to Login</Button>
              )}
              {step < STEPS.length - 1 ? (
                <Button className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-full gap-1" onClick={() => canProceed() && setStep(step + 1)} disabled={!canProceed()} data-testid="apply-next">
                  Next <ChevronRight size={14} />
                </Button>
              ) : (
                <Button className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full gap-1 px-6" onClick={handleSubmit} disabled={submitting} data-testid="apply-submit">
                  <Send size={14} /> {submitting ? 'Submitting...' : 'Submit Application'}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-muted-foreground mt-6">
          Our Square is a program of Purpose Driven. Your information is kept confidential.
        </p>
      </div>
    </div>
  );
}
